import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useState } from 'react';
import type { DecisionEngineJourneyDto } from '../../api/admin';
import { buildPdlHardStopBreakdown } from '../../lib/pdlAdjudicatorDeclineCopy';
import { buildPdlEligibilityExplanation } from '../../lib/pdlEligibilityExplainer';
import { pdlDeclineAlertPulseSx } from '../../lib/pdlDecisionChrome';
import {
  PDL_AMOUNT_STRIP,
  PDL_BREAKDOWN_DIALOG_SUBTITLE,
  PDL_BREAKDOWN_DIALOG_TITLE,
} from '../../lib/pdlAdjudicatorUiCopy';
import { buildPdlLegacyGridCalculation } from '../../lib/pdlLegacyGridCalculation';
import { parsePdlCurrency } from '../../lib/pdlCapacityWaterfall';
import { PdlLegacyGridCalculationVisual } from './PdlLegacyGridCalculationVisual';

type Props = {
  decisionEngine?: DecisionEngineJourneyDto | null;
  applicationDeclineReason?: string | null;
};

function AmountTile({
  label,
  value,
  emphasize,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', letterSpacing: '0.02em' }}>
        {label}
      </Typography>
      <Typography
        variant={emphasize ? 'h6' : 'body1'}
        sx={{
          fontWeight: emphasize ? 800 : 700,
          lineHeight: 1.25,
          color: emphasize ? 'success.main' : 'text.primary',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export function PdlEligibilityHelpButton({ decisionEngine, applicationDeclineReason }: Props) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const explanation = buildPdlEligibilityExplanation(decisionEngine, applicationDeclineReason);
  const legacyCalc = buildPdlLegacyGridCalculation(decisionEngine?.pdlAffordability);

  if (!explanation && !legacyCalc) return null;

  const decision = String(explanation?.decision ?? decisionEngine?.decision ?? '—').toUpperCase();
  const decisionColor =
    decision === 'APPROVE' ? 'success' : decision === 'DECLINE' ? 'error' : 'warning';
  const declined = decision === 'DECLINE';
  const declineKey = (
    applicationDeclineReason ??
    decisionEngine?.declinedReason ??
    ''
  ).trim();
  const hardStop =
    declined && declineKey
      ? buildPdlHardStopBreakdown({
          declineReason: declineKey,
          tableLimit: legacyCalc?.legacyLimit ?? null,
        })
      : null;

  const requested = explanation?.amountComparison.requested ?? null;
  const approved = explanation?.amountComparison.approved ?? null;
  const tableLimit =
    legacyCalc?.legacyLimit != null
      ? new Intl.NumberFormat('en-CA', {
          style: 'currency',
          currency: 'CAD',
          maximumFractionDigits: 0,
        }).format(legacyCalc.legacyLimit)
      : null;

  const showStrip = requested || tableLimit || approved;

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<CalculateOutlinedIcon sx={{ fontSize: '1rem !important' }} />}
        onClick={() => setOpen(true)}
        data-testid="pdl-eligibility-help-button"
        data-tour-target="pdl-eligibility-help-button"
        sx={{
          fontWeight: 700,
          whiteSpace: 'nowrap',
          borderColor: 'divider',
          color: 'text.primary',
          '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.04) },
        }}
      >
        Full breakdown
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        data-testid="pdl-eligibility-help-dialog"
      >
        <DialogTitle sx={{ pr: 6, pb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.05rem', color: 'primary.dark' }}>
              {PDL_BREAKDOWN_DIALOG_TITLE}
            </Typography>
            <Chip size="small" color={decisionColor} label={decision} sx={{ height: 22, fontWeight: 800 }} />
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, lineHeight: 1.45 }}>
            {PDL_BREAKDOWN_DIALOG_SUBTITLE}
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={() => setOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 1.25, pb: 2 }}>
          <Stack spacing={1.25}>
            {showStrip ? (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  py: 0.5,
                }}
                data-testid="pdl-breakdown-amount-strip"
              >
                {requested ? <AmountTile label={PDL_AMOUNT_STRIP.requested} value={requested} /> : null}
                {tableLimit ? (
                  <AmountTile label="Table limit" value={tableLimit} emphasize />
                ) : null}
                {approved &&
                parsePdlCurrency(approved) !== legacyCalc?.legacyLimit ? (
                  <AmountTile label={PDL_AMOUNT_STRIP.approved} value={approved} />
                ) : null}
              </Box>
            ) : null}

            {legacyCalc ? <PdlLegacyGridCalculationVisual model={legacyCalc} /> : null}

            {hardStop ? (
              <Alert
                severity="error"
                sx={{
                  borderRadius: 1.5,
                  ...pdlDeclineAlertPulseSx(theme),
                  '& .MuiAlert-message': { width: '100%' },
                }}
                data-testid="pdl-breakdown-decline-stop"
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 800, lineHeight: 1.35, color: 'error.dark' }}
                >
                  {hardStop.title}
                </Typography>
                <Stack spacing={0.45} sx={{ mt: 0.5 }}>
                  {hardStop.lines.map((line) => (
                    <Typography
                      key={line}
                      variant="body2"
                      sx={{ lineHeight: 1.5, color: 'error.dark', fontWeight: 500 }}
                    >
                      {line}
                    </Typography>
                  ))}
                </Stack>
              </Alert>
            ) : null}

            {!legacyCalc && !hardStop ? (
              <Typography variant="body2" color="text.secondary">
                Legacy scoring trace not available for this evaluation.
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
